import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import { siteInfo } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      name: "关于我们 - 黑料专区",
      description: siteInfo.intro,
      inLanguage: "zh-CN",
      url: "https://leiysp.cn/about",
    },
    {
      "@type": "Organization",
      name: siteInfo.name,
      url: "https://leiysp.cn/",
      description: siteInfo.tagline,
      email: siteInfo.email,
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "客户支持",
        email: siteInfo.email,
        availableLanguage: ["zh-CN"],
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://leiysp.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "关于我们",
          item: "https://leiysp.cn/about",
        },
      ],
    },
  ],
};

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 黑料专区是怎么做话题的 | 黑料专区",
    description:
      "了解黑料专区的品牌定位、内容原则与讨论氛围。我们围绕公开信息整理吃瓜话题，鼓励瓜友理性交流、观点碰撞，让围观也能聊出价值。",
    keywords: "黑料专区,关于我们,吃瓜讨论,内容原则,瓜友交流",
    path: "/about",
    ogType: "website",
    jsonLd,
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li>
                  <Link to="/" className="transition-colors hover:text-primary-600">
                    首页
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line"></i>
                </li>
                <li className="text-foreground-800">关于我们</li>
              </ol>
            </nav>

            <h1 className="mt-4 max-w-3xl font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
              我们是怎么做话题的
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-8 text-foreground-600">
              {siteInfo.intro}
            </p>

            <dl className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-4">
              {siteInfo.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <dt className="text-xs text-foreground-600">{stat.label}</dt>
                  <dd className="mt-1 font-heading text-2xl font-bold text-foreground-950">
                    {stat.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            <article>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">
                我们的定位
              </h2>
              <p className="mt-4 text-sm leading-8 text-foreground-700">
                黑料专区既不是爆料场，也不是单向资讯站。它更像一个把话题摊开的讨论桌：
                编辑负责把公开信息整理清楚，把来龙去脉、各方说法和后续进展放在同一个页面里。
              </p>
              <p className="mt-4 text-sm leading-8 text-foreground-700">
                至于怎么评价，交给瓜友。同一件事，有人关注表达，有人关注节奏，有人只想围观。
                这些角度都值得被看见，而不是被淹没在零散的评论区里。
              </p>
            </article>

            <article>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">
                发展理念
              </h2>
              <p className="mt-4 text-sm leading-8 text-foreground-700">
                娱乐话题更新很快，热闹来得也快。我们不追求抢第一，而是希望等事情稍微清晰一点，
                再把它整理成能读得下去的内容。慢一点，但更完整。
              </p>
              <p className="mt-4 text-sm leading-8 text-foreground-700">
                同时，我们相信讨论本身有价值。一个话题真正的生命力，不在于当天有多热，
                而在于过一阵子回看，大家是否还觉得这段讨论有意思。
              </p>
            </article>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              内容原则
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              这四条是编辑和社区共同遵守的底线，也是我们做每一个话题页时的判断依据。
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {siteInfo.principles.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            用户口碑
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {siteInfo.testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <i className="ri-double-quotes-l text-2xl text-primary-400"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-7 text-foreground-700">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-700">
                    {item.name.slice(0, 1)}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-foreground-950">{item.name}</span>
                    <span className="text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 pb-16 md:px-6">
          <div className="grid grid-cols-1 gap-8 rounded-lg bg-secondary-900 px-6 py-10 md:px-12 md:py-12 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                联系我们
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-7 text-background-200">
                对话题整理有建议、发现信息需要更正，或者想推荐一个值得聊的话题，都欢迎随时找到我们。
              </p>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4 rounded-lg bg-secondary-800 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <div className="flex flex-col">
                  <span className="text-xs text-background-300">合作与反馈邮箱</span>
                  <span className="text-sm font-medium text-background-50">{siteInfo.email}</span>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-lg bg-secondary-800 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                  <i className="ri-time-line text-xl"></i>
                </span>
                <div className="flex flex-col">
                  <span className="text-xs text-background-300">回复时间</span>
                  <span className="text-sm font-medium text-background-50">{siteInfo.updateNote}</span>
                </div>
              </div>
              <Link
                to="/topics"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-chat-3-line"></i>
                去话题广场看看
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}